import {
  RestrictionType,
  PaymentExclusionOverrideSetting,
  isBuyerAccountInRestrictionList,
} from '@arc/unified-payments-frontend/shared-payment';
import { createSelector } from '@ngrx/store';
import {
  selectPaymentInclusionExclusionRules,
  selectAllowCreditCard,
  selectAllowACH,
  selectIsBuyerAccountRestrictionEnabledForOriginSystem,
} from '../payment-rules';
import { selectDocumentsBuyerAccountNumbers } from '../session-store';

export const selectPaymentExclusionOverrideSetting = createSelector(
  selectPaymentInclusionExclusionRules,
  selectDocumentsBuyerAccountNumbers,
  selectIsBuyerAccountRestrictionEnabledForOriginSystem,
  (
    restrictionConfig,
    buyerAccounts,
    isBuyerAccountRestrictionEnabledForOriginSystem,
  ) => {
    const restrictionType = restrictionConfig?.restrictionType;
    const result: PaymentExclusionOverrideSetting = {
      isBuyerAccountInRestrictionList: false,
      allowRestrictionOverride: false,
    };

    if (
      !isBuyerAccountRestrictionEnabledForOriginSystem ||
      !restrictionType ||
      restrictionType === RestrictionType.NoRestriction
    ) {
      return result;
    }

    // Check if any of the accounts are in the restriction lists
    const isAccountInRestrictionList = isBuyerAccountInRestrictionList(
      buyerAccounts,
      restrictionConfig?.restrictionConfigurations,
      restrictionType,
    );

    result.isBuyerAccountInRestrictionList =
      (restrictionType === RestrictionType.Inclusion &&
        !isAccountInRestrictionList) ||
      (restrictionType === RestrictionType.Exclusion &&
        isAccountInRestrictionList);

    // Allow override only if the account is restricted and the override is allowed
    result.allowRestrictionOverride =
      result.isBuyerAccountInRestrictionList &&
      restrictionConfig?.overrideRestriction;

    return result;
  },
);

export const selectAllowCardOverride = createSelector(
  selectPaymentExclusionOverrideSetting,
  (state) => state.allowRestrictionOverride,
);

export const selectHasAvailablePaymentMethods = createSelector(
  selectAllowACH,
  selectAllowCreditCard,
  selectAllowCardOverride,
  (allowBankPayments, allowCardPayments, allowOverrideCardPayments) => {
    return allowBankPayments || allowCardPayments || allowOverrideCardPayments;
  },
);

export const selectIsBuyerAccountRestricted = createSelector(
  selectPaymentExclusionOverrideSetting,
  (state) => state.isBuyerAccountInRestrictionList,
);

export const selectShowPaymentToggle = createSelector(
  selectAllowACH,
  selectAllowCreditCard,
  selectIsBuyerAccountRestricted,
  (acceptACH, acceptCreditCard, isBuyerAccountRestricted) => {
    return acceptACH && acceptCreditCard && !isBuyerAccountRestricted;
  },
);
